import { CONST } from '@common/constants/const.constant';
import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import { createCollection } from '@common/utils/create-collection.util';
import { customNotify } from '@common/utils/custom-notify.util';
import { createPaymentSourceDto, IPaymentSourceDto } from '@src/api/model/payment-source.dto';
import { createSubscriptionDto, ISubscriptionDto } from '@src/api/model/subscription.dto';
import {
    IEmail,
    IEmailValidationResponse,
    IPaymentIntent,
    IPaymentSource,
    IPaymentSourceRequestBody,
    IPublicKeys,
    IRegisteredResponse,
    IRegisterRequest,
    ISetupIntent,
    registrationApiRequest,
    TRegistrationV1CustomerLatestSubscriptionGetError,
    TRegistrationV1CustomerMarketingPreferencesGetError,
    TRegistrationV1CustomerMarketingPreferencesPatchError,
    TRegistrationV1CustomerTrackAliasPostError,
    TRegistrationV1PaymentIntentPostError,
    TRegistrationV1PaymentSourcePostError,
    TRegistrationV1PublicKeysGetError,
    TRegistrationV1RegisterPostError,
    TRegistrationV1ValidateEmailPostError,
    TRegistrationV1OrderEstimatePostError,
    IOrderEstimateRequest,
    IOrderEstimateResponse,
    IMarketingPreferences,
    ITrackAliasRequestBody,
} from '@src/api/registration-generated-api';
import { queryClient } from '@src/shared/query-client';
import { useEffect } from 'react';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { apiHooks } from '../api-hooks';

const NAME = 'registration';
const useValidateEmail = (): UseMutationResult<
    IEmailValidationResponse,
    IBEError<TRegistrationV1ValidateEmailPostError>,
    IEmail
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1ValidateEmailPost(props).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-validate-email`,
        },
    );
};

const _useCreatePaymentIndent = (): UseMutationResult<IPaymentIntent, IBEError<TRegistrationV1PaymentIntentPostError>> => {
    return useMutation(
        () => {
            return registrationApiRequest.registrationV1PaymentIntentPost().then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-create-payment-indent`,
        },
    );
};

const useAttachPaymentMethodToCustomer = (): UseMutationResult<
    IPaymentIntent,
    IBEError<TRegistrationV1PaymentIntentPostError>,
    { id: string; body: IPaymentSourceRequestBody }
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1PaymentSourceIdPatch(props.id, props.body, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-create-payment-indent`,
            onSuccess: async () => {
                queryClient.refetchQueries(`${NAME}-payment-source`);
            },
        },
    );
};

const useAttachPaymentButtonMethodToCustomer = (): UseMutationResult<
    IPaymentSource,
    IBEError<TRegistrationV1PaymentSourcePostError>,
    IPaymentSourceRequestBody
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1PaymentSourcePost(props, {}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-stripe-confirm-indent`,
            onSuccess: async () => {
                queryClient.refetchQueries(`${NAME}-payment-source`);
            },
        },
    );
};

const useCreateSetupIndent = (): UseMutationResult<ISetupIntent, IBEError<TRegistrationV1PaymentIntentPostError>, void> => {
    return useMutation(
        () => {
            return registrationApiRequest.registrationV1StripeSetupIntentPost().then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-stripe-get-indent`,
        },
    );
};

const useCreateSetupIndentAuthorized = (): UseMutationResult<
    ISetupIntent,
    IBEError<TRegistrationV1PaymentIntentPostError>,
    void
> => {
    return useMutation(
        () => {
            return registrationApiRequest.registrationV1StripeSetupIntentPut({}).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-stripe-get-indent-useCreateSetupIndentAuthorized`,
        },
    );
};

const useGetPaymentSources = (
    queryOptions?: UseQueryOptions,
): UseQueryResult<ICollection<IPaymentSourceDto>, IBEError<TRegistrationV1PublicKeysGetError>> => {
    return useQuery<ICollection<IPaymentSourceDto>, IBEError<TRegistrationV1PublicKeysGetError>>(
        `${NAME}-payment-source`,
        () => {
            return registrationApiRequest.registrationV1PaymentSourceGet({}).then((r) =>
                createCollection(
                    r.data.list?.map((l) =>
                        createPaymentSourceDto((l as unknown as { payment_source: IPaymentSource }).payment_source),
                    ),
                    0,
                    {},
                ),
            );
        },
        queryOptions,
    );
};

const useGetChargeableKeys = (
    queryOptions?: UseQueryOptions,
): UseQueryResult<IPublicKeys, IBEError<TRegistrationV1PublicKeysGetError>> => {
    return useQuery<IPublicKeys, IBEError<TRegistrationV1PublicKeysGetError>>(
        `${NAME}-chargebee-keys`,
        () => {
            return registrationApiRequest.registrationV1PublicKeysGet().then((r) => r.data);
        },
        queryOptions,
    );
};

const useTrackCustomer = (): UseMutationResult<
    void,
    IBEError<TRegistrationV1CustomerTrackAliasPostError>,
    { appClientId: string; body: ITrackAliasRequestBody }
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1CustomerTrackAliasPost(props.body).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-track`,
        },
    );
};

const useUpdateMarketingPreferences = (): UseMutationResult<
    void,
    IBEError<TRegistrationV1CustomerMarketingPreferencesPatchError>,
    { body: IMarketingPreferences }
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1CustomerMarketingPreferencesPatch(props.body).then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-track`,
        },
    );
};

const useRegister = (): UseMutationResult<
    IRegisteredResponse,
    IBEError<TRegistrationV1RegisterPostError>,
    { appClientId: string; data: IRegisterRequest }
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest
                .registrationV1RegisterPost(props.data, { ['App-Client-Id']: props.appClientId })
                .then((r) => r.data);
        },
        {
            mutationKey: `${NAME}-register`,
        },
    );
};

const usePartialRegister = (): UseMutationResult<
    IRegisteredResponse,
    IBEError<TRegistrationV1RegisterPostError>,
    { appClientId: string; data: IRegisterRequest }
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest
                .registrationV1SignUpPost(props.data, { ['App-Client-Id']: props.appClientId })
                .then((r) => r.data);
        },
        { mutationKey: `${NAME}-partialRegister` },
    );
};

const useGetLastSubscription = (
    queryOptions?: UseQueryOptions,
): UseQueryResult<ISubscriptionDto, IBEError<TRegistrationV1CustomerLatestSubscriptionGetError>> => {
    const {
        error: productError,
        isFetched: productFetched,
        isLoading: productLoading,
        remove: productRemove,
    } = apiHooks.packHouse.useGetProducts();

    const {
        error: deliveryConfigError,
        isFetched: deliveryFetched,
        isLoading: deliveryConfigLoading,
        remove: deliveryConfigRemove,
    } = apiHooks.packHouse.useGetDeliveryConfigurationList({ limit: 1000 }, queryOptions);

    const {
        error: skipsError,
        isFetched: skipsFetched,
        isLoading: skipsLoading,
        remove: skipRemove,
    } = apiHooks.item.useGetSkips(queryOptions);

    const {
        error: orderError,
        isFetched: orderFetched,
        isLoading: orderLoading,
        remove: orderRemove,
    } = apiHooks.item.useGetInfiniteOrderList(() => {
        return {
            limit: CONST.LIST_LIMIT,
        };
    }, queryOptions || {});

    useEffect(() => {
        customNotify.error([
            { beError: productError, onShown: productRemove },
            { beError: deliveryConfigError, onShown: deliveryConfigRemove },
            { beError: skipsError, onShown: skipRemove },
            { beError: orderError, onShown: orderRemove },
        ]);
    }, [
        productError,
        deliveryConfigError,
        skipsError,
        orderError,
        productRemove,
        deliveryConfigRemove,
        skipRemove,
        orderRemove,
    ]);

    const result = useQuery<ISubscriptionDto, IBEError<TRegistrationV1CustomerLatestSubscriptionGetError>>(
        `${NAME}-used`,
        () => {
            return registrationApiRequest
                .registrationV1CustomerLatestSubscriptionGet()
                .then((res) => createSubscriptionDto({ s1: res.data.subscription }));
        },
        {
            enabled:
                productFetched &&
                deliveryFetched &&
                skipsFetched &&
                (queryOptions?.enabled !== undefined ? queryOptions.enabled : true) &&
                orderFetched,
            ...queryOptions,
        },
    );
    const isLoading = productLoading || deliveryConfigLoading || skipsLoading || result.isLoading || orderLoading;
    return { ...result, isFetching: isLoading };
};

const useGetMarketingPreferences = (
    queryOptions?: UseQueryOptions,
): UseQueryResult<IMarketingPreferences, IBEError<TRegistrationV1CustomerMarketingPreferencesGetError>> => {
    return useQuery<IMarketingPreferences, IBEError<TRegistrationV1CustomerMarketingPreferencesGetError>>(
        `${NAME}-marketing-preferences`,
        () => {
            return registrationApiRequest
                .registrationV1CustomerMarketingPreferencesGet()
                .then((r) => (r.data));
        },
        queryOptions,
    );
};

const useEstimateOrderInfo = (): UseMutationResult<
    IOrderEstimateResponse,
    IBEError<TRegistrationV1OrderEstimatePostError>,
    { body: IOrderEstimateRequest }
> => {
    return useMutation(
        (props) => {
            return registrationApiRequest.registrationV1OrderEstimatePost(props.body).then((r) => r.data).then((data) =>  queryClient.setQueryData(`${NAME}-order`, (): IOrderEstimateResponse => data));
        },
        {
            mutationKey: `${NAME}-order`,
        },
    );
};

const useGetEstimateOrderInfo = (
    body: IOrderEstimateRequest,
    queryOptions?: UseQueryOptions,
): UseQueryResult<
    IOrderEstimateResponse,
    IBEError<TRegistrationV1OrderEstimatePostError>
    > => {
        return useQuery<
    IOrderEstimateResponse,
    IBEError<TRegistrationV1OrderEstimatePostError>>(
        `${NAME}-order`,
        () => {
            return registrationApiRequest.registrationV1OrderEstimatePost(body).then((r) => r.data);
        },
        queryOptions,
    );
    };


    
export const registration = {
    useCreateSetupIndent,
    useCreateSetupIndentAuthorized,
    useValidateEmail,
    useGetMarketingPreferences,
    useGetChargeableKeys,
    useTrackCustomer,
    useUpdateMarketingPreferences,
    useRegister,
    usePartialRegister,
    useGetPaymentSources,
    useAttachPaymentMethodToCustomer,
    useAttachPaymentButtonMethodToCustomer,
    useGetLastSubscription,
    useEstimateOrderInfo,
    useGetEstimateOrderInfo
};
